import { baseApi, SHOPS_TAG, USERS_TAG } from '@/shared';
import { shopsAdapter } from '../utils/helpers/adapter';

import type {
  TShop,
  TShopCreateParams,
  TShopUpdateParams,
  TShopAddCurrencyParams,
} from './types';

export const shopsApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getShops: build.query<TShop[], void>({
      query: () => ({
        url: '/admin/shops/list',
      }),
      transformResponse: shopsAdapter,
      providesTags: [SHOPS_TAG],
    }),

    createShop: build.mutation<void, TShopCreateParams>({
      query: (params: TShopCreateParams) => ({
        url: '/admin/shops/create',
        method: 'POST',
        body: {
          name: params.name,
          currencies: params.currencies,
          type: params.type,
          partner_commissions: params.partnerCommissions.map((commission) => ({
            currency: commission.currency,
            commission_percent: commission.commissionPercent,
            calculation_base: commission.calculationBase,
          })),
          callback_url: params.callbackUrl,
          owner_id: params.ownerId,
          homepage_url: params.homepageUrl,
          success_url: params.successUrl,
          failure_url: params.failureUrl,
        },
      }),
      invalidatesTags: [SHOPS_TAG, USERS_TAG],
    }),

    updateShop: build.mutation<void, TShopUpdateParams>({
      query: ({ shopId, ...params }: TShopUpdateParams) => ({
        url: `/admin/shops/update/${shopId}`,
        method: 'PUT',
        body: {
          name: params.name,
          callback_url: params.callbackUrl,
          owner_id: params.ownerId,
          homepage_url: params.homepageUrl,
          success_url: params.successUrl,
          failure_url: params.failureUrl,
          partner_commissions: params.partnerCommissions?.map((commission) => ({
            currency: commission.currency,
            commission_percent: commission.commissionPercent,
            calculation_base: commission.calculationBase,
          })),
        },
      }),
      invalidatesTags: [SHOPS_TAG],
    }),

    deleteShop: build.mutation<void, { shopId: number }>({
      query: ({ shopId }) => ({
        url: `/admin/shops/delete/${shopId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [SHOPS_TAG, USERS_TAG],
    }),

    addShopCurrency: build.mutation<void, TShopAddCurrencyParams>({
      query: ({ shopId, ...params }: TShopAddCurrencyParams) => ({
        url: `/admin/shops/add_currency/${shopId}`,
        method: 'POST',
        params: params,
      }),
      invalidatesTags: [SHOPS_TAG],
    }),

    activateShop: build.mutation<void, { shopId: number }>({
      query: ({ shopId }) => ({
        url: `/admin/shops/activate/${shopId}`,
        method: 'PUT',
      }),
      invalidatesTags: [SHOPS_TAG],
    }),

    deactivateShop: build.mutation<void, { shopId: number }>({
      query: ({ shopId }) => ({
        url: `/admin/shops/deactivate/${shopId}`,
        method: 'PUT',
      }),
      invalidatesTags: [SHOPS_TAG],
    }),

  }),
});

export const {
  useGetShopsQuery,
  useCreateShopMutation,
  useUpdateShopMutation,
  useDeleteShopMutation,
  useAddShopCurrencyMutation,
  useActivateShopMutation,
  useDeactivateShopMutation,
} = shopsApi;
